import useSidebarState from '../../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { get, onValue, ref } from 'firebase/database';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { database } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { translateDepartment } from '../../utils/translationUtils';
import './MyTeamWorkspace.css';
import './EmployeeLearningPathwayPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || 'http://localhost:8000';

const emptyPathway = {
  status: 'idle',
  summary: { strengths: [], areasToImprove: [], managerFeedback: '' },
  steps: [],
  tools: []
};

const makeProject = () => ({
  id: `project-${Date.now()}`,
  title: 'New project',
  type: 'Exercise',
  description: '',
  sourceUrl: ''
});

const autosizeTextarea = (element) => {
  if (!element) return;
  const input = element;
  input.style.height = 'auto';
  input.style.height = `${input.scrollHeight}px`;
};

const EmployeeLearningPathwayPage = () => {
  useUiLanguage();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL, language } = useLanguage();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [companyUID, setCompanyUID] = useState('');
  const [employee, setEmployee] = useState(null);
  const [draft, setDraft] = useState(emptyPathway);
  const [selectedStepId, setSelectedStepId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [draggedStepId, setDraggedStepId] = useState('');
  const [dragOverStepId, setDragOverStepId] = useState('');
  const [companyLoading, setCompanyLoading] = useState(true);
  const [employeeLoading, setEmployeeLoading] = useState(true);

  useEffect(() => {
    const resolveCompany = async () => {
      if (!currentUser) {
        setCompanyLoading(false);
        return;
      }
      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) {
          setCompanyUID(currentUser.uid);
          return;
        }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          const collaborator = collabSnap.val();
          if (collaborator?.companyId) setCompanyUID(collaborator.companyId);
        }
      } catch (err) {
        console.error('Company resolution error:', err);
        setError(t('employeePage.learningPathways.companyError', 'Could not resolve company access.'));
      } finally {
        setCompanyLoading(false);
      }
    };
    resolveCompany();
  }, [currentUser, t]);

  useEffect(() => {
    if (!companyUID || !employeeId) {
      if (!companyLoading) setEmployeeLoading(false);
      return undefined;
    }
    setEmployeeLoading(true);
    const employeeRef = ref(database, `companies/${companyUID}/employees/${employeeId}`);
    return onValue(employeeRef, (snap) => {
      const nextEmployee = snap.val();
      setEmployee(nextEmployee || null);
      setEmployeeLoading(false);

      if (!dirty) {
        const nextPathway = nextEmployee?.learningPathway || emptyPathway;
        const nextSteps = Array.isArray(nextPathway.steps) ? nextPathway.steps : [];
        setDraft({
          ...emptyPathway,
          ...nextPathway,
          summary: { ...emptyPathway.summary, ...(nextPathway.summary || {}) },
          steps: nextSteps,
          tools: Array.isArray(nextPathway.tools) ? nextPathway.tools : []
        });

        setSelectedStepId((current) => (
          current && nextSteps.some((step) => step.id === current) ? current : (nextSteps[0]?.id || '')
        ));
        setActiveTab((current) => {
          if (current === 'overview') return current;
          return nextSteps.some((step) => step.id === current) ? current : 'overview';
        });
      }
    }, () => {
      setEmployeeLoading(false);
    });
  }, [companyLoading, companyUID, dirty, employeeId]);

  const profile = employee?.profile || {};
  const mobility = employee?.mobility || {};
  const documents = employee?.documents || {};
  const departmentLabel = translateDepartment(profile.department || employee?.department, language);
  const steps = useMemo(() => (Array.isArray(draft.steps) ? draft.steps : []), [draft.steps]);
  const selectedStep = steps.find((step) => step.id === selectedStepId) || steps[0] || null;
  const selectedCourseCount = steps.filter((step) => step.selectedCourseId).length;
  const documentsList = Object.entries(documents.files || {}).map(([id, doc]) => ({ id, ...doc }));
  const hasUploadedCv = documentsList.some((doc) => {
    const type = String(doc.type || '').toLowerCase();
    const name = String(doc.name || '').toLowerCase();
    return type === 'resume' || type === 'cv' || name.includes('resume') || name.includes('cv');
  });
  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  const isPageLoading = companyLoading || employeeLoading;

  useEffect(() => {
    if (!steps.length) {
      if (selectedStepId) setSelectedStepId('');
      if (activeTab !== 'overview') setActiveTab('overview');
      return;
    }

    if (!selectedStepId || !steps.some((step) => step.id === selectedStepId)) {
      setSelectedStepId(steps[0].id);
    }

    if (activeTab !== 'overview' && !steps.some((step) => step.id === activeTab)) {
      setActiveTab(steps[0].id);
    }
  }, [activeTab, selectedStepId, steps]);

  const updateDraft = (updater) => {
    setDraft((current) => (typeof updater === 'function' ? updater(current) : updater));
    setDirty(true);
  };

  const updateSelectedStep = (patch) => {
    if (!selectedStep) return;
    updateDraft((current) => ({
      ...current,
      steps: (current.steps || []).map((step) => (
        step.id === selectedStep.id ? { ...step, ...patch } : step
      ))
    }));
  };

  const updateProject = (projectId, patch) => {
    if (!selectedStep) return;
    updateSelectedStep({
      projects: (selectedStep.projects || []).map((project) => (
        project.id === projectId ? { ...project, ...patch } : project
      ))
    });
  };

  const moveStep = (targetStepId) => {
    if (!draggedStepId || draggedStepId === targetStepId) return;
    updateDraft((current) => {
      const nextSteps = [...(current.steps || [])];
      const fromIndex = nextSteps.findIndex((step) => step.id === draggedStepId);
      const toIndex = nextSteps.findIndex((step) => step.id === targetStepId);
      if (fromIndex === -1 || toIndex === -1) return current;
      const [moved] = nextSteps.splice(fromIndex, 1);
      nextSteps.splice(toIndex, 0, moved);
      return { ...current, steps: nextSteps };
    });
    setDraggedStepId('');
    setDragOverStepId('');
  };

  const openStepTab = (stepId) => {
    setSelectedStepId(stepId);
    setActiveTab(stepId);
  };

  const handleGenerate = async () => {
    if (!companyUID || !employeeId || !currentUser?.uid) return;
    setGenerating(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/employee/learning-pathway/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          userUID: currentUser.uid,
          userEmail: currentUser.email || '',
          generatedBy: currentUser.email || currentUser.uid
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to generate learning pathway');
      const nextPathway = data.pathway || emptyPathway;
      setDraft(nextPathway);
      setSelectedStepId(nextPathway.steps?.[0]?.id || '');
      setActiveTab('overview');
      setDirty(false);
    } catch (err) {
      console.error('Learning pathway generation failed:', err);
      setError(err.message || 'Failed to generate learning pathway');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!companyUID || !employeeId || !currentUser?.uid) return;
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/employee/learning-pathway`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          userUID: currentUser.uid,
          userEmail: currentUser.email || '',
          updatedBy: currentUser.email || currentUser.uid,
          pathway: draft
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to save learning pathway');
      setDraft(data.pathway || draft);
      setDirty(false);
    } catch (err) {
      console.error('Learning pathway save failed:', err);
      setError(err.message || 'Failed to save learning pathway');
    } finally {
      setSaving(false);
    }
  };

  const renderSummaryCard = (title, values, icon, tone = 'blue', itemIcon = 'fa-circle') => (
    <div className={`lp-card lp-summary-card ${tone}`}>
      <div className="lp-summary-head">
        <i className={`fa-solid ${icon}`}></i>
        <span>{title}</span>
      </div>
      {(values || []).length ? (
        (values || []).map((value, index) => (
          <div key={`${value}-${index}`} className="lp-summary-item">
            <i className={`fa-solid ${itemIcon}`}></i>
            <span>{value}</span>
          </div>
        ))
      ) : (
        <div className="lp-empty-copy">{t('common:status.empty', 'Empty')}</div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--app-background, #031128)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <main
        className={`lp-page-shell${isRTL ? ' rtl' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{ '--sidebar-w': sidebarWidth }}
      >
        {isPageLoading ? (
          <div className="data-analytics-loading-screen">
            <i className="fa-solid fa-spinner fa-spin" />
            <p>{t('common:status.pleaseWait', 'Please Wait')}</p>
          </div>
        ) : (
          <>
        <div className="lp-header-row">
          <div className="lp-header-copy">
            <button className="lp-back-btn" onClick={() => navigate(`/my-team/employee/${employeeId}`)}>
              <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`}></i>
              <span>{t('employeePage.back', 'Back to My Team')}</span>
            </button>
            <h1>{t('employeePage.learningPathways.fullTitle', 'Employee Learning Pathway')}</h1>
            <p>
              {profile.name || employee?.name || '—'} · {profile.roleTitle || employee?.role || '—'}
              {departmentLabel ? ` · ${departmentLabel}` : ''}
            </p>
          </div>

          <div className="lp-header-actions">
            <button
              className="lp-header-btn lp-header-btn-primary"
              onClick={handleGenerate}
              disabled={!hasUploadedCv || generating}
            >
              <i className={`fa-solid ${generating ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`}></i>
              {draft.status === 'completed'
                ? t('employeePage.learningPathways.regenerate', 'Regenerate')
                : t('employeePage.learningPathways.generate', 'Generate')}
            </button>
            <button
              className={`lp-header-btn${dirty && steps.length ? ' active' : ''}`}
              onClick={handleSave}
              disabled={!dirty || saving || !steps.length}
            >
              <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`}></i>
              {saving ? t('common:status.saving', 'Saving...') : t('common:actions.save', 'Save')}
            </button>
          </div>
        </div>

        {(error || draft.error || !hasUploadedCv) && (
          <div className={`lp-alert-banner${error || draft.error ? ' danger' : ' warning'}`}>
            <i className={`fa-solid ${error || draft.error ? 'fa-triangle-exclamation' : 'fa-circle-info'}`}></i>
            <span>{error || draft.error || t('employeePage.learningPathways.noCv', 'Upload a resume/CV in Files before generating a plan.')}</span>
          </div>
        )}

        <section className="lp-tab-strip" aria-label={t('employeePage.learningPathways.steps', 'Steps')}>
          <button
            type="button"
            className={`lp-tab-btn${activeTab === 'overview' ? ' active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('common:overview', 'Overview')}
          </button>
          {steps.map((step, index) => (
            <button
              key={step.id}
              type="button"
              className={`lp-tab-btn${activeTab === step.id ? ' active' : ''}`}
              onClick={() => openStepTab(step.id)}
            >
              <span className="lp-tab-index">{index + 1}</span>
              <span className="lp-tab-text">
                {step.title || t('employeePage.learningPathways.stepLabel', { index: index + 1, defaultValue: `Step ${index + 1}` })}
              </span>
            </button>
          ))}
        </section>

        {activeTab === 'overview' ? (
          <>
            <section className="lp-metrics-grid">
              <div className="lp-card lp-metric-card">
                <span className="lp-kicker">{t('employeePage.learningPathways.currentRole', 'Current role')}</span>
                <strong>{draft.target?.currentRole || profile.roleTitle || employee?.role || '—'}</strong>
              </div>
              <div className="lp-card lp-metric-card">
                <span className="lp-kicker">{t('employeePage.learningPathways.targetRole', 'Target role')}</span>
                <strong>{draft.target?.targetRole || mobility.targetRole || profile.roleTitle || employee?.role || '—'}</strong>
              </div>
              <div className="lp-card lp-metric-card">
                <span className="lp-kicker">{t('employeePage.learningPathways.selectedCourses', 'Selected courses')}</span>
                <strong>{selectedCourseCount} / {steps.length}</strong>
              </div>
            </section>

            <section className="lp-summary-grid">
              {renderSummaryCard(t('employeePage.learningPathways.strengths', 'Strengths'), draft.summary?.strengths, 'fa-shield-halved', 'green', 'fa-circle-check')}
              {renderSummaryCard(t('employeePage.learningPathways.areasToImprove', 'Areas to improve'), draft.summary?.areasToImprove, 'fa-chart-simple', 'amber', 'fa-circle-exclamation')}
              <div className="lp-card lp-summary-card blue">
                <div className="lp-summary-head">
                  <i className="fa-solid fa-user-tie"></i>
                  <span>{t('employeePage.learningPathways.managerFeedback', 'Manager note')}</span>
                </div>
                <textarea
                  className="lp-textarea lp-textarea-auto"
                  rows={1}
                  ref={autosizeTextarea}
                  value={draft.summary?.managerFeedback || ''}
                  onInput={(event) => autosizeTextarea(event.target)}
                  onChange={(event) => updateDraft((current) => ({
                    ...current,
                    summary: { ...(current.summary || {}), managerFeedback: event.target.value }
                  }))}
                />
              </div>
            </section>

            <section className="lp-overview-grid">
              <div className="lp-card lp-steps-overview-card">
                <div className="lp-rail-header">
                  <div>
                    <h2>{t('employeePage.learningPathways.steps', 'Steps')}</h2>
                    <p>{t('employeePage.learningPathways.dragHint', 'Drag to reorder')}</p>
                  </div>
                  <div className="lp-step-count">{steps.length}</div>
                </div>

                {steps.length ? steps.map((step, index) => (
                  <button
                    key={step.id}
                    type="button"
                    draggable
                    onDragStart={() => setDraggedStepId(step.id)}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (draggedStepId && draggedStepId !== step.id && dragOverStepId !== step.id) {
                        setDragOverStepId(step.id);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverStepId === step.id) setDragOverStepId('');
                    }}
                    onDrop={() => moveStep(step.id)}
                    onDragEnd={() => {
                      setDraggedStepId('');
                      setDragOverStepId('');
                    }}
                    onClick={() => openStepTab(step.id)}
                    className={`lp-step-item compact${selectedStep?.id === step.id ? ' selected' : ''}${dragOverStepId === step.id ? ' drag-over' : ''}${draggedStepId === step.id ? ' dragging' : ''}`}
                  >
                    <span className="lp-step-order">{String(index + 1).padStart(2, '0')}</span>
                    <div className="lp-step-copy">
                      <span className="lp-step-kicker">{step.type}</span>
                      <strong>{step.title}</strong>
                      <span>{step.profileTag || t('employeePage.learningPathways.coursePicker', 'Course picker')}</span>
                    </div>
                    <i className="fa-solid fa-grip-vertical lp-step-grip"></i>
                  </button>
                )) : (
                  <div className="lp-rail-empty">
                    {t('employeePage.learningPathways.emptyEditor', 'Generate a pathway to edit steps, courses, and projects.')}
                  </div>
                )}
              </div>

              <div className="lp-overview-side">
                <div className="lp-card lp-overview-note-card">
                  <div className="lp-section-head">
                    <div>
                      <h3>{t('employeePage.learningPathways.fullTitle', 'Employee Learning Pathway')}</h3>
                      <p>{t('employeePage.learningPathways.selectedCourses', 'Selected courses')} {selectedCourseCount} / {steps.length}</p>
                    </div>
                  </div>
                  <div className="lp-overview-note-list">
                    <div className="lp-overview-note-item">
                      <span className="lp-kicker">{t('employeePage.learningPathways.currentRole', 'Current role')}</span>
                      <strong>{draft.target?.currentRole || profile.roleTitle || employee?.role || '—'}</strong>
                    </div>
                    <div className="lp-overview-note-item">
                      <span className="lp-kicker">{t('employeePage.learningPathways.targetRole', 'Target role')}</span>
                      <strong>{draft.target?.targetRole || mobility.targetRole || profile.roleTitle || employee?.role || '—'}</strong>
                    </div>
                  </div>
                </div>

                {!!(draft.tools || []).length && (
                  <div className="lp-card lp-tools-wrap">
                    <div className="lp-section-head">
                      <div className="lp-section-head-with-count">
                        <h3>{t('employeePage.learningPathways.tools', 'Tools')}</h3>
                      </div>
                      <div className="lp-step-count">{(draft.tools || []).length}</div>
                    </div>
                    <div className="lp-tools-row">
                      {(draft.tools || []).map((tool) => (
                        <a
                          key={tool.id || tool.name}
                          className="lp-tool-pill"
                          href={tool.detailsUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa-solid fa-toolbox"></i>
                          {tool.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        ) : (
          <section className="lp-workspace-single">
            <div className="lp-card lp-editor-panel">
              {selectedStep ? (
                <>
                  <div className="lp-editor-topbar">
                    <div>
                      <span className="lp-kicker">
                        {t('employeePage.learningPathways.stepLabel', {
                          index: steps.findIndex((step) => step.id === selectedStep.id) + 1,
                          defaultValue: 'Step 1'
                        })}
                      </span>
                      <h2>{selectedStep.title}</h2>
                    </div>
                    <div className="lp-editor-pill">
                      {selectedStep.selectedCourseId
                        ? t('employeePage.learningPathways.selectedCourses', 'Selected courses')
                        : t('employeePage.learningPathways.coursePicker', 'Course picker')}
                    </div>
                  </div>

                  <div className="lp-field-grid">
                    <label className="lp-field-block">
                      <span className="lp-field-label">{t('employeePage.learningPathways.stepTitle', 'Step title')}</span>
                      <input
                        className="lp-input"
                        value={selectedStep.title || ''}
                        onChange={(event) => updateSelectedStep({ title: event.target.value })}
                      />
                    </label>
                    <label className="lp-field-block compact">
                      <span className="lp-field-label">{t('employeePage.learningPathways.stepType', 'Type')}</span>
                      <select
                        className="lp-select"
                        value={selectedStep.type || 'skill'}
                        onChange={(event) => updateSelectedStep({ type: event.target.value })}
                      >
                        <option value="skill">{uiText("skill")}</option>
                        <option value="methodology">{uiText("methodology")}</option>
                        <option value="tooling">{uiText("tooling")}</option>
                      </select>
                    </label>
                  </div>

                  <label className="lp-field-block">
                    <span className="lp-field-label">{t('employeePage.learningPathways.profileTag', 'Profile tag')}</span>
                    <input
                      className="lp-input"
                      value={selectedStep.profileTag || ''}
                      onChange={(event) => updateSelectedStep({ profileTag: event.target.value })}
                    />
                  </label>

                  <div className="lp-field-grid two-up">
                    <label className="lp-field-block">
                      <span className="lp-field-label">{t('employeePage.learningPathways.whatYouLearn', 'What they learn')}</span>
                      <textarea
                        className="lp-textarea"
                        value={selectedStep.whatYouLearn || ''}
                        onChange={(event) => updateSelectedStep({ whatYouLearn: event.target.value })}
                      />
                    </label>
                    <label className="lp-field-block">
                      <span className="lp-field-label">{t('employeePage.learningPathways.whyItMatters', 'Why it matters')}</span>
                      <textarea
                        className="lp-textarea"
                        value={selectedStep.whyItMatters || ''}
                        onChange={(event) => updateSelectedStep({ whyItMatters: event.target.value })}
                      />
                    </label>
                  </div>

                  <div className="lp-section-head">
                    <div>
                      <h3>{t('employeePage.learningPathways.coursePicker', 'Course picker')}</h3>
                      <p>{t('employeePage.learningPathways.selectedCourses', 'Selected courses')} {selectedStep.selectedCourseId ? '1' : '0'}</p>
                    </div>
                  </div>

                  <div className="lp-course-grid">
                    {(selectedStep.suggestedCourses || []).map((course) => (
                      <label
                        key={course.id}
                        className={`lp-course-card${selectedStep.selectedCourseId === course.id ? ' selected' : ''}`}
                      >
                        <div className="lp-course-select">
                          <input
                            type="radio"
                            checked={selectedStep.selectedCourseId === course.id}
                            onChange={() => updateSelectedStep({ selectedCourseId: course.id })}
                          />
                        </div>
                        <div className="lp-course-copy">
                          <strong>{course.courseName}</strong>
                          <span>{[course.provider, course.pricing, course.rating].filter(Boolean).join(' · ')}</span>
                          {course.rationale && <p>{course.rationale}</p>}
                          {course.link && (
                            <a href={course.link} target="_blank" rel="noopener noreferrer">
                              {t('employeePage.learningPathways.openCourse', 'Open course')}
                              <i className="fa-solid fa-arrow-up-right-from-square"></i>
                            </a>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="lp-section-head spaced">
                    <div>
                      <h3>{t('employeePage.learningPathways.projects', 'Projects and case studies')}</h3>
                      <p>{(selectedStep.projects || []).length} {t('employeePage.learningPathways.steps', 'Steps').toLowerCase()}</p>
                    </div>
                    <button
                      type="button"
                      className="lp-btn-ghost"
                      onClick={() => updateSelectedStep({ projects: [...(selectedStep.projects || []), makeProject()] })}
                    >
                      <i className="fa-solid fa-plus"></i>
                      {t('employeePage.learningPathways.addProject', 'Add project')}
                    </button>
                  </div>

                  {(selectedStep.projects || []).map((project) => (
                    <div key={project.id} className="lp-project-card">
                      <div className="lp-project-head">
                        <input
                          className="lp-input"
                          value={project.title || ''}
                          onChange={(event) => updateProject(project.id, { title: event.target.value })}
                        />
                        <select
                          className="lp-select compact"
                          value={project.type || 'Exercise'}
                          onChange={(event) => updateProject(project.id, { type: event.target.value })}
                        >
                          <option value="Exercise">{uiText("Exercise")}</option>
                          <option value="Case Study">{uiText("Case Study")}</option>
                        </select>
                        <button
                          type="button"
                          className="lp-icon-danger"
                          title={t('employeePage.learningPathways.removeProject', 'Remove project')}
                          onClick={() => updateSelectedStep({ projects: (selectedStep.projects || []).filter((item) => item.id !== project.id) })}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                      <textarea
                        className="lp-textarea tall"
                        value={project.description || ''}
                        onChange={(event) => updateProject(project.id, { description: event.target.value })}
                      />
                      <input
                        className="lp-input"
                        placeholder={t('employeePage.learningPathways.sourceUrl', 'Source URL')}
                        value={project.sourceUrl || ''}
                        onChange={(event) => updateProject(project.id, { sourceUrl: event.target.value })}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <div className="lp-editor-empty">
                  {t('employeePage.learningPathways.emptyEditor', 'Generate a pathway to edit steps, courses, and projects.')}
                </div>
              )}
            </div>
          </section>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default EmployeeLearningPathwayPage;
